import endpoints from '@app/src/api/endpoints'
import Permissions from '@app/src/auth/permissions'
import { usePermissions } from '@app/src/auth/permissions/usePermissions'
import useActorTypes from '@app/src/hooks/useActorTypes'
import CountryFilterSelect from '@app/src/pages/Configurations/ConfigurationsPages/Common/CountryFilterSelect'
import FinalRiskRatingFilterSelect from '@app/src/pages/Configurations/ConfigurationsPages/Common/FinalRiskRatingFilterSelect'
import ProviderCategoryFilterSelect from '@app/src/pages/Configurations/ConfigurationsPages/Common/ProviderCategoryFilterSelect'
import CustomCategoriesFilters from '@app/src/pages/ResourceCollection/Filters/CustomCategoriesFilters'
import Filter from '@app/src/pages/ResourceCollection/Filters/Filter'
import FilterSection from '@app/src/pages/ResourceCollection/Filters/FilterSection'
import FilterSelect from '@app/src/pages/ResourceCollection/Filters/FilterSelect'
import OptionWithLabel from '@app/src/pages/ResourceCollection/Filters/OptionWithLabel'
import { Operators } from '@app/src/pages/ResourceCollection/Filters/useFilters'
import { ProviderStandardCategoryNames } from '@app/src/types/categories'
import { MAPPING_NODE_TIER_OPTIONS } from '@app/src/utils/providerCategoryOptions'
import { groupOptionsContiguously } from '@app/src/utils/groupOptionsContiguously'
import { Box, Chip } from '@mui/material'
import { useFlags } from 'launchdarkly-react-client-sdk'
import React from 'react'
import { useIntl } from 'react-intl'

const ProviderCategoryFilterDrawer: React.FC = () => {
  const { formatMessage } = useIntl()
  const { renderWithPermission } = usePermissions()
  const { productMapping } = useFlags()
  const { items: actorTypes } = useActorTypes()

  return (
    <>
      <FilterSection label={formatMessage({ id: 'schemas.filter.companies' })}>
        <CountryFilterSelect
          facetsEndpoint={endpoints.providersWithFacets}
          facetsParamName="country.iso3166Alpha2"
          disableAutoFilter={false}
        />
      </FilterSection>
      <FilterSection defaultCollapsed label={formatMessage({ id: 'schemas.filter.additionalInformation' })}>
        <Box mb={2}>
          <ProviderCategoryFilterSelect categoryName={ProviderStandardCategoryNames.ActivityStatus} />
        </Box>
        <Box mb={2}>
          <FinalRiskRatingFilterSelect />
        </Box>
        <Box mb={2}>
          <ProviderCategoryFilterSelect categoryName={ProviderStandardCategoryNames.Priority} />
        </Box>
        <Box mb={2}>
          <ProviderCategoryFilterSelect categoryName={ProviderStandardCategoryNames.ApprovalStatus} />
        </Box>
        {renderWithPermission({
          [Permissions.SOURCING_USER]: (
            <>
              <Box mb={2}>
                <ProviderCategoryFilterSelect categoryName={ProviderStandardCategoryNames.SupplierUsage} />
              </Box>
              <Box mb={2}>
                <ProviderCategoryFilterSelect categoryName={ProviderStandardCategoryNames.Tier} />
              </Box>
            </>
          ),
        })}
      </FilterSection>
      <CustomCategoriesFilters />

      {productMapping && (
        <FilterSection defaultCollapsed label={formatMessage({ id: 'navbar.productMapping' })}>
          <Filter operator={Operators.In} name="mappingNodes.actorType.id">
            {({ value, onChange }) => (
              <FilterSelect
                size="small"
                multiple
                groupBy={option => option.additionalText ?? ''}
                renderOption={(props, option, { selected }) => (
                  <OptionWithLabel
                    key={Number(option.value)}
                    props={props}
                    label={option?.label}
                    multiple
                    selected={selected}
                  />
                )}
                options={groupOptionsContiguously(
                  actorTypes.map(actorType => ({
                    label: formatMessage({ id: `schemas.mappingNode.actorTypeValues.${actorType.name}` }),
                    value: actorType.id,
                    additionalText: actorType.actorTypeGroup
                      ? formatMessage({ id: `schemas.mappingNode.actorTypeGroups.${actorType.actorTypeGroup}` })
                      : formatMessage({ id: `schemas.product.productIndustry.${actorType.industry}` }),
                  })),
                  option => option.additionalText,
                )}
                renderTags={(value, getTagProps) =>
                  value?.map(
                    (option, index) =>
                      option && (
                        <Chip
                          {...getTagProps({ index })}
                          key={option?.value?.toString() || index}
                          label={option?.label}
                          size="small"
                        />
                      ),
                  )
                }
                onChange={value => onChange(value)}
                fieldLabel={formatMessage({ id: 'schemas.mappingNode.actorType' })}
                value={[value].flat()}
              />
            )}
          </Filter>
          <Filter operator={Operators.In} name="mappingNodes.tier">
            {({ value, onChange }) => (
              <FilterSelect
                size="small"
                multiple
                options={
                  MAPPING_NODE_TIER_OPTIONS?.map(tier => ({
                    label: tier.toString(),
                    value: tier.toString(),
                  })) ?? []
                }
                onChange={value => onChange(value)}
                fieldLabel={formatMessage({ id: 'schemas.mappingNode.tier' })}
                value={[value].flat()}
              />
            )}
          </Filter>
        </FilterSection>
      )}
    </>
  )
}
export default ProviderCategoryFilterDrawer
